import React from 'react';
import {observer} from 'mobx-react-lite';
import {Button, ButtonVariant} from '../../components';
import {createStylesHook, variance} from '../../styling';
import {sized} from '../../Svg';
import GiftBoxSvg from '../../assets/svg/colored/giftBox.svg';
import {View, Text, StyleSheet, Image, Pressable} from 'react-native';
import CloseSvg from '../../assets/svg/colorless/close.svg';
import {ModalBackdrop} from '../../modals/components';
import {useRoot, useStrings} from '../../Root/hooks';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {CryptoLoader} from '../../components/CryptoLoader';
import {LG_BREAKPOINT} from '../../units/constants';
import {expr} from 'mobx-utils';

export interface FreeMinerBottomModalProps {
  onClose: () => void;
  isLoading: boolean;
}

export default observer((props: FreeMinerBottomModalProps) => {
  const styles = useStyles();
  const strings = useStrings();
  const insets = useSafeAreaInsets();
  const {windowDimensionsState} = useRoot();
  const isMd = expr(() => windowDimensionsState.window.width > 768);
  return (
    <RootView>
      <Wrapper>
        <ModalBackdrop onPress={props.onClose} />
        <Content style={{paddingBottom: insets.bottom}}>
          <CloseIcon style={styles.close} onPress={props.onClose} />
          <Center>
            <IconWrapper>
              <BoxIcon />
            </IconWrapper>
            <Title> {strings['dashboard.freeMiner.bottomSheet.desc']}</Title>
            <ImageWrapper>
              <Image
                source={require('../../assets/img_bonus-miner.png')}
                style={styles.image}
                resizeMode="contain"
              />
            </ImageWrapper>
          </Center>
          <ActionContainer>
            <Pressable onPress={props.onClose}>
              <Button variant={ButtonVariant.Highlighted}>
                {strings['dashboard.freeMiner.button']}
              </Button>
            </Pressable>
          </ActionContainer>
        </Content>
      </Wrapper>
      {props.isLoading && (
        <RootView>
          <ModalBackdrop onPress={props.onClose} />
          <Loader style={{bottom: !isMd ? insets.bottom + 115 : '50%'}}>
            <CryptoLoader />
          </Loader>
        </RootView>
      )}
    </RootView>
  );
});
const useStyles = createStylesHook((theme) => ({
  close: {
    position: 'absolute',
    zIndex: 2,
    top: 10,
    right: 10,
    color: '#B3B3B3',
    cursor: 'pointer',
    ...theme.mediaQuery({
      [LG_BREAKPOINT]: {
        top: 15,
        right: 15,
      },
    }),
  },
  image: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
}));
const RootView = variance(View)(() => ({
  root: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
  },
}));
const ImageWrapper = variance(View)(() => ({
  root: {
    width: '100%',
    aspectRatio: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 430,
  },
}));
const Wrapper = variance(View)(() => ({
  root: {
    flex: 1,
    justifyContent: 'flex-end',
  },
}));
const Loader = variance(View)(() => ({
  root: {
    position: 'absolute',
    flex: 1,
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
const Content = variance(View)((theme) => ({
  root: {
    backgroundColor: theme.palette.background,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    paddingTop: 30,
    justifyContent: 'center',
    ...theme.mediaQuery({
      [LG_BREAKPOINT]: {
        margin: 'auto',
        maxWidth: 460,
        borderRadius: 10,
        paddingTop: 24,
      },
    }),
  },
}));
const Center = variance(View)((theme) => ({
  root: {
    paddingHorizontal: 7.5,
    alignItems: 'center',
    ...theme.mediaQuery({
      [LG_BREAKPOINT]: {
        paddingHorizontal: 24,
      },
    }),
  },
}));

const IconWrapper = variance(View)(() => ({
  root: {
    marginBottom: 15,
    alignItems: 'center',
  },
}));
const ActionContainer = variance(View)((theme) => ({
  root: {
    backgroundColor: theme.palette.background,
    padding: 15,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 0},
    shadowOpacity: 0.1,
    shadowRadius: 5,
    ...theme.mediaQuery({
      [LG_BREAKPOINT]: {
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
        elevation: 0,
        shadowColor: 'transparent',
        shadowOffset: {width: 0, height: 0},
        shadowOpacity: 0,
        shadowRadius: 0,
        paddingHorizontal: 24,
        paddingBottom: 24,
      },
    }),
  },
}));
const Title = variance(Text)((theme) => ({
  root: {
    color: theme.palette.textPrimary,
    textAlign: 'center',
    fontSize: 20,
    flexWrap: 'wrap',
    ...theme.fontByWeight('700'),
    lineHeight: 28,
    marginBottom: 30,
  },
}));
const BoxIcon = sized(GiftBoxSvg, 80);
const CloseIcon = sized(CloseSvg, 20);
